// MACHINERY V2 (Stroje) — THE v2 FORM FIELD KIT (M2.6).
//
// >>> SHARED V2 PRIMITIVE. IMPORT IT — DO NOT REINVENT. <<<
// M2.6 (SR create/edit) is its first consumer; M2.7's AddMachinePicker +
// CustomMachineDialog are the next. The kit is CO-OWNED with the Creative
// Designer (SHARED_DEPLOY_AND_RULES canonical decision 2) — coordinate prop
// changes through BREAKING_CHANGES.md.
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.6 — "teal glyph + mono
// eyebrow, 38px inputs, teal focus". Reference impl:
// design_handoff_machinery_v3/machinery-sr.jsx `FormField` (lines 639-651).
// Artboards 0702 (Refined Claude) / 0703 (MachineID Native) — ONE component,
// both palettes, driven by the root's `.p-claude` / `.p-machineid` vars.
//
// WHAT IS IN THE KIT AND WHY (the "promote only what is actually shared" rule):
//   FormField      — label row + child control + error slot. Every field on
//                    every v2 form draws this.
//   FormSublabel   — the second-level mono label the date/time grid needs
//                    (`DÁTUM OTVORENIA` / `ČAS`); a plain class, not a component,
//                    because it is one span with no behaviour.
//   FormError      — the red message under a control. Its own export because a
//                    field can have TWO controls with one error (the date/time
//                    grid) and because the file input's error is not an RHF one.
// The 38px input itself is a CLASS (`.mv2-input` in formFields.scss), not a
// component: a wrapper component would have to forward refs for RHF's
// `register()` and re-expose every native attribute, buying nothing.
//
// The style sheet is imported here, ONCE, as a side effect — the same
// import-once discipline MachineryV2Layout uses for the token sheet.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import "./formFields.scss";

export interface FormFieldProps {
  /** 13px teal glyph from FormGlyphs.tsx. */
  glyph?: React.ReactNode;
  /** Already-translated eyebrow text (the artboards draw it uppercase). */
  label: string;
  required?: boolean;
  /** Already-translated error message; renders the red line under the control. */
  error?: string;
  /** Wires the control's aria-describedby to the error line. */
  htmlFor?: string;
  children: React.ReactNode;
}

/** The red asterisk the artboard puts after a required label. */
const RequiredMark: React.FC = () => (
  <span style={{ color: "var(--red)", marginLeft: 3 }} aria-hidden>
    *
  </span>
);

export const FormError: React.FC<{ id?: string; message?: string }> = ({ id, message }) => {
  if (!message) return null;
  return (
    <div
      id={id}
      role="alert"
      style={{ marginTop: 5, fontSize: 11.5, color: "var(--red)", minWidth: 0 }}
    >
      {message}
    </div>
  );
};

const FormField: React.FC<FormFieldProps> = ({
  glyph,
  label,
  required,
  error,
  htmlFor,
  children,
}) => (
  <div style={{ marginBottom: 16, minWidth: 0 }}>
    <label
      htmlFor={htmlFor}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginBottom: 6,
        minWidth: 0,
      }}
    >
      {glyph && (
        <span style={{ color: "var(--mod-accent)", display: "inline-flex", flexShrink: 0 }}>
          {glyph}
        </span>
      )}
      <span className="eyebrow" style={{ color: "var(--mod-accent)" }}>
        {label}
        {required && <RequiredMark />}
      </span>
    </label>
    {children}
    <FormError id={htmlFor ? htmlFor + "-error" : undefined} message={error} />
  </div>
);

export default FormField;
